/**
 * Generic pagination-aware caching utility
 * Uses IndexDB for direct persistence via idb-keyval
 * Supports TTL-based cache expiration and pattern-based invalidation
 */

import {
  del as idbDel,
  get as idbGet,
  keys as idbKeys,
  set as idbSet,
} from 'idb-keyval';

export interface CacheEntry<T> {
  data: T[];
  total: number;
  hasMore: boolean;
  timestamp: number;
}

export interface CacheOptions {
  ttl?: number; // Time-to-live in milliseconds (undefined = no expiration)
  persist?: boolean; // Enable IndexDB persistence
  storageKeyPrefix?: string; // Prefix for IndexDB keys
}

interface RequiredCacheOptions {
  ttl: number | undefined;
  persist: boolean;
  storageKeyPrefix: string;
}

export class PaginatedCache<T> {
  private options: RequiredCacheOptions;

  constructor(options: CacheOptions = {}) {
    this.options = {
      ttl: options.ttl,
      persist: options.persist ?? true,
      storageKeyPrefix: options.storageKeyPrefix ?? 'paginated-cache',
    };
  }

  /**
   * Get cached entry if it exists and is not stale
   */
  async get(key: string): Promise<CacheEntry<T> | null> {
    if (!this.options.persist) {
      return null;
    }

    try {
      const entry = await idbGet(this.getStorageKey(key));

      if (!entry) {
        return null;
      }

      // Check if cache entry is stale
      if (this.isStale(entry)) {
        await this.invalidate(key);
        return null;
      }

      return entry;
    } catch (error) {
      console.error(
        `Failed to get cache entry: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
      return null;
    }
  }

  /**
   * Set cache entry
   */
  async set(key: string, entry: CacheEntry<T>): Promise<void> {
    try {
      const newEntry = {
        ...entry,
        timestamp: Date.now(),
      };

      if (this.options.persist) {
        await idbSet(this.getStorageKey(key), newEntry);
      }
    } catch (error) {
      console.error(
        `Failed to set cache entry: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  /**
   * Check if key exists in cache
   */
  async has(key: string): Promise<boolean> {
    if (!this.options.persist) {
      return false;
    }

    try {
      const entry = await idbGet(this.getStorageKey(key));

      if (!entry) {
        return false;
      }

      // Check if stale
      if (this.isStale(entry)) {
        await this.invalidate(key);
        return false;
      }

      return true;
    } catch (error) {
      console.error(
        `Failed to check cache existence: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
      return false;
    }
  }

  /**
   * Check if cache entry is stale based on TTL
   */
  private isStale(entry: CacheEntry<T>): boolean {
    if (!this.options.ttl) {
      return false; // No TTL means never stale
    }

    return Date.now() - entry.timestamp > this.options.ttl;
  }

  /**
   * Remove single cache entry
   */
  async invalidate(key: string): Promise<void> {
    try {
      if (this.options.persist) {
        await idbDel(this.getStorageKey(key));
      }
    } catch (error) {
      console.error(
        `Failed to invalidate cache entry: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  /**
   * Remove all cache entries matching a regex pattern
   */
  async invalidatePattern(pattern: RegExp): Promise<void> {
    try {
      if (!this.options.persist) {
        return;
      }

      const allKeys = await idbKeys();
      const keysToRemove = allKeys.filter((key) => {
        if (typeof key !== 'string') {
          return false;
        }
        if (!key.startsWith(this.options.storageKeyPrefix)) {
          return false;
        }
        const cacheKey = key.substring(
          this.options.storageKeyPrefix.length + 1,
        );
        return pattern.test(cacheKey);
      });

      if (keysToRemove.length === 0) {
        return;
      }

      await Promise.all(keysToRemove.map((key) => idbDel(key as string)));
    } catch (error) {
      console.error(
        `Failed to invalidate pattern: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  /**
   * Clear entire cache
   */
  async invalidateAll(): Promise<void> {
    try {
      if (!this.options.persist) {
        return;
      }

      const allKeys = await idbKeys();
      const keysToDelete = allKeys.filter((key) => {
        if (typeof key !== 'string') {
          return false;
        }
        return key.startsWith(this.options.storageKeyPrefix);
      });

      if (keysToDelete.length === 0) {
        return;
      }

      await Promise.all(keysToDelete.map((key) => idbDel(key as string)));
    } catch (error) {
      console.error(
        `Failed to clear cache: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  /**
   * Generate deterministic cache key from parameters
   */
  generateKey(params: Record<string, string | number>): string {
    const sortedKeys = Object.keys(params).sort();
    return sortedKeys.map((key) => `${params[key]}`).join('_');
  }

  /**
   * Get IndexDB key for cache entry
   */
  private getStorageKey(key: string): string {
    return `${this.options.storageKeyPrefix}:${key}`;
  }
}
